import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, ShieldCheck } from "lucide-react";
import api, { setToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";

export default function SetupAdminDialog({ open, onClose, onSuccess }) {
  const [familyPwd, setFamilyPwd] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setFamilyPwd(""); setFullName(""); setEmail(""); setPwd(""); };
  const close = () => { if (!loading) { reset(); onClose(); } };

  const submit = async () => {
    if (!familyPwd) return toast.error("Inserisci il codice famiglia");
    if (!fullName.trim()) return toast.error("Inserisci il tuo nome");
    if (!email.trim()) return toast.error("Inserisci la tua email");
    if (pwd.length < 8) return toast.error("Password personale: minimo 8 caratteri");
    setLoading(true);
    try {
      const res = await api.post("/auth/setup-admin", {
        family_password: familyPwd,
        email: email.trim().toLowerCase(),
        password: pwd,
        full_name: fullName.trim(),
      });
      setToken(res.data.token);
      setStoredUser(res.data.user);
      toast.success(`Admin creato! Benvenuto/a ${res.data.user.full_name}`);
      reset();
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="setup-admin-dialog" className="max-w-md rounded-3xl border-[#E8E1D9] bg-white">
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <ShieldCheck className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
              Primo accesso Admin
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            Rivendica il tuo account admin
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Inserisci il codice famiglia attuale e crea il tuo account personale. Sarai l'admin e potrai invitare gli altri.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Codice famiglia attuale *</label>
            <Input data-testid="setup-admin-family-pwd" type="password" value={familyPwd} onChange={(e) => setFamilyPwd(e.target.value)} placeholder="••••••••" className="rounded-xl border-[#E8E1D9]" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Il tuo nome *</label>
            <Input data-testid="setup-admin-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Riccardo Rossi" className="rounded-xl border-[#E8E1D9]" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Email *</label>
            <Input data-testid="setup-admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="riccardo@example.com" className="rounded-xl border-[#E8E1D9]" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Password personale (min 8) *</label>
            <Input data-testid="setup-admin-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" className="rounded-xl border-[#E8E1D9]" />
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={close} disabled={loading} className="rounded-full border-[#E8E1D9]">Annulla</Button>
          <Button data-testid="setup-admin-submit" type="button" onClick={submit} disabled={loading} className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]">
            {loading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Crea admin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
